import { Fragment, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useFormContext } from "react-hook-form";
import { classNames } from "../utils/formatters";

const companies = ["Todas", "INS", "Mapfre", "Assa", "Quálitas", "Oceánica"];
const selectableCompanies = companies.slice(1); // All companies except "Todas"

export default function InsuranceCompanySelect({ value, onChange, disabled }) {
  const { formState } = useFormContext();
  const { errors } = formState;
  const [query, setQuery] = useState("");
  const selected = value || [];

  const handleChange = (newSelection) => {
    if (!Array.isArray(newSelection)) return;

    let updatedSelection;
    // If "Todas" is being toggled
    if (newSelection.includes("Todas")) {
      // If all companies are currently selected, unselect all
      if (selected.length === selectableCompanies.length) {
        updatedSelection = [];
      } else {
        // Otherwise, select all companies
        updatedSelection = [...selectableCompanies];
      }
    } else {
      updatedSelection = newSelection;
    }

    onChange(updatedSelection);
  };

  const filteredCompanies =
    query === ""
      ? companies
      : companies.filter((company) =>
          company.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <div className="">
      <label
        htmlFor="insuranceCompany"
        className="block text-sm font-medium text-gray-700"
      >
        Compañía de seguros
      </label>
      <Combobox
        value={selected}
        onChange={handleChange}
        multiple
        disabled={disabled}
      >
        <div className="relative mt-1">
          <div
            className={classNames(
              "relative w-full cursor-default overflow-hidden rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 block border border-gray-300 sm:text-sm",
              errors.company
                ? "ring-2 ring-red-500 ring-offset-2 ring-offset-white"
                : ""
            )}
          >
            <Combobox.Button as="div">
              <Combobox.Input
                className={classNames(
                  "w-full border-none py-2 pl-3 pr-10 text-input leading-5 text-gray-900 focus:ring-0 normal-case",
                  disabled ? "bg-gray-100" : ""
                )}
                displayValue={(companies: string[]) =>
                  companies.length === selectableCompanies.length
                    ? "Todas"
                    : companies.join(", ")
                }
                onChange={(event) => setQuery(event.target.value)}
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
              {filteredCompanies.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Compañía no encontrada
                </div>
              ) : (
                filteredCompanies.map((company) => (
                  <Combobox.Option
                    key={company}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 px-4 ${
                        active ? "bg-orange-500 text-white" : "text-gray-900"
                      }`
                    }
                    value={company}
                  >
                    {({ selected: isSelected, active }) => (
                      <>
                        <span
                          className={`block truncate ${
                            isSelected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {company}
                          {company === "Todas" &&
                            selected.length === selectableCompanies.length &&
                            " ✓"}
                        </span>
                        {isSelected ||
                        (company === "Todas" &&
                          selected.length === selectableCompanies.length) ? (
                          <span
                            className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                              active ? "text-white" : "text-orange-500"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
